import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import TrailTimeline from '../../components/trails/TrailTimeline';
import TrailEventModal from '../../components/trails/TrailEventModal';
import {
  Shield,
  Search,
  Filter,
  Download,
  Calendar,
  User,
  RefreshCw } from
'lucide-react';

const API_URL = API_BASE;

const AuditTrail = () => {const { t } = useTranslation("wms");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filters, setFilters] = useState({
    entityType: 'all',
    entityId: '',
    actorId: '',
    dateRange: '24h'
  });

  useEffect(() => {
    fetchEvents();
  }, [filters.dateRange]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ezerpToken');
      // For now, use mock data since we query by entity
      // In production, we'd have a dedicated audit endpoint
      setEvents(getMockAuditEvents());
    } catch (error) {
      console.error('Failed to fetch audit events:', error);
      setEvents(getMockAuditEvents());
    }
    setLoading(false);
  };

  const getMockAuditEvents = () => [
  {
    id: 'AUD-001',
    event_type: 'AUDIT.USER.LOGIN',
    trail_type: 'AUDIT',
    timestamp_server: new Date(Date.now() - 60000).toISOString(),
    actor_id: 'operator@ezerp.com',
    actor_type: 'USER',
    entity_type: 'USER',
    entity_id: 'user-3',
    payload: {
      action_type: 'LOGIN',
      ip_address: '192.168.1.100',
      device_info: { browser: 'Chrome', os: 'Windows' }
    }
  },
  {
    id: 'AUD-002',
    event_type: 'AUDIT.INVENTORY.ADJUSTED',
    trail_type: 'AUDIT',
    timestamp_server: new Date(Date.now() - 300000).toISOString(),
    actor_id: 'manager@ezerp.com',
    actor_type: 'USER',
    entity_type: 'INVENTORY',
    entity_id: 'INV-ADJ-001',
    payload: {
      action_type: 'ADJUSTMENT',
      before_state: { qty: 100 },
      after_state: { qty: 95 },
      reason: 'Cycle count discrepancy'
    }
  },
  {
    id: 'AUD-003',
    event_type: 'AUDIT.ORDER.STATUS_CHANGED',
    trail_type: 'AUDIT',
    timestamp_server: new Date(Date.now() - 600000).toISOString(),
    actor_id: 'system',
    actor_type: 'SYSTEM',
    entity_type: 'ORDER',
    entity_id: 'ORD-2026-001',
    payload: {
      action_type: 'STATUS_CHANGE',
      before_state: { status: 'PICKING' },
      after_state: { status: 'PACKED' }
    }
  },
  {
    id: 'AUD-004',
    event_type: 'AUDIT.PRODUCT.UPDATED',
    trail_type: 'AUDIT',
    timestamp_server: new Date(Date.now() - 900000).toISOString(),
    actor_id: 'admin@ezerp.com',
    actor_type: 'USER',
    entity_type: 'PRODUCT',
    entity_id: 'SKU-001',
    payload: {
      action_type: 'UPDATE',
      before_state: { price: 29.99 },
      after_state: { price: 34.99 },
      fields_changed: ['price']
    }
  },
  {
    id: 'AUD-005',
    event_type: 'AUDIT.WAREHOUSE.CONFIG_CHANGED',
    trail_type: 'AUDIT',
    timestamp_server: new Date(Date.now() - 1800000).toISOString(),
    actor_id: 'admin@ezerp.com',
    actor_type: 'USER',
    entity_type: 'WAREHOUSE',
    entity_id: 'WH-001',
    payload: {
      action_type: 'CONFIG_UPDATE',
      before_state: { zones: 3 },
      after_state: { zones: 4 },
      change_description: 'Added Zone D for cold storage'
    }
  }];


  const handleSearch = () => {
    // Filter events based on current filters
    const filtered = getMockAuditEvents().filter((event) => {
      if (filters.entityType !== 'all' && event.entity_type !== filters.entityType) return false;
      if (filters.entityId && !event.entity_id.includes(filters.entityId)) return false;
      if (filters.actorId && !event.actor_id.includes(filters.actorId)) return false;
      return true;
    });
    setEvents(filtered);
  };

  return (
    <div className="p-6 space-y-6" data-testid="audit-trail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
            <Shield className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{t("auditTrail", "Audit Trail")}</h1>
            <p className="text-muted-foreground">{t("whoWhatWhen", "Who, what, when, where - compliance records")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchEvents}>
            <RefreshCw className="h-4 w-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />{t("export", "Export")}

          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-end gap-4">
            <div className="space-y-2 flex-1">
              <label className="text-sm font-medium">{t("entityType", "Entity Type")}</label>
              <Select
                value={filters.entityType}
                onValueChange={(v) => setFilters((prev) => ({ ...prev, entityType: v }))}>
                
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allTypes", "All Types")}</SelectItem>
                  <SelectItem value="USER">{t("user", "User")}</SelectItem>
                  <SelectItem value="ORDER">{t("order", "Order")}</SelectItem>
                  <SelectItem value="INVENTORY">{t("inventory", "Inventory")}</SelectItem>
                  <SelectItem value="PRODUCT">{t("product", "Product")}</SelectItem>
                  <SelectItem value="WAREHOUSE">{t("warehouse", "Warehouse")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 flex-1">
              <label className="text-sm font-medium">{t("entityId", "Entity ID")}</label>
              <Input
                placeholder="Search by ID..."
                value={filters.entityId}
                onChange={(e) => setFilters((prev) => ({ ...prev, entityId: e.target.value }))} />
              
            </div>
            <div className="space-y-2 flex-1">
              <label className="text-sm font-medium">{t("actor", "Actor")}</label>
              <Input
                placeholder="User or system..."
                value={filters.actorId}
                onChange={(e) => setFilters((prev) => ({ ...prev, actorId: e.target.value }))} />
              
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("period", "Period")}</label>
              <Select
                value={filters.dateRange}
                onValueChange={(v) => setFilters((prev) => ({ ...prev, dateRange: v }))}>
                
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">{t("last24h", "Last 24h")}</SelectItem>
                  <SelectItem value="7d">{t("last7Days", "Last 7 days")}</SelectItem>
                  <SelectItem value="30d">{t("last30Days", "Last 30 days")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />{t("search", "Search")}

            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t("showing", "Showing")}
          <strong>{events.length}</strong>{t("auditEvents", "audit events")}
        </p>
        <Badge variant="outline">
          <Calendar className="h-3 w-3 mr-1" />
          {filters.dateRange}
        </Badge>
      </div>

      {/* Timeline */}
      <Card>
        <CardContent className="p-6">
          {loading ?
          <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
              <p className="mt-2 text-muted-foreground">{t("loadingEvents", "Loading events...")}</p>
            </div> :

          <TrailTimeline
            events={events}
            onEventClick={setSelectedEvent}
            defaultView="vertical" />

          }
        </CardContent>
      </Card>

      {/* Event Detail Modal */}
      {selectedEvent &&
      <TrailEventModal
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)} />

      }
    </div>);

};

export default AuditTrail;